import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { WORKSPACE_BY_ID, workspaceFor, workspaceNavigation } from "@alm/policy";
import { DESK_QUERY } from "@/components/app/agent-pane/use-agent-pane";
import { OliviaWorkspace } from "@/components/olivia/olivia-workspace";
import { useMedia } from "@/hooks/use-wide";
import { openPaneAgent } from "@/lib/workspace/agent-pane";
import { usePolicy } from "@/lib/policy";
import { lastPlaces } from "@/lib/workspace/workspace-memory";

interface OliviaSearch {
  c?: string;
  /** A question to start from, put in the box and never sent: the person sends it. */
  ask?: string;
}

export const Route = createFileRoute("/_authenticated/ws/olivia/")({
  validateSearch: (search: Record<string, unknown>): OliviaSearch => ({
    ...(typeof search.c === "string" && search.c ? { c: search.c } : {}),
    ...(typeof search.ask === "string" && search.ask ? { ask: search.ask.slice(0, 2000) } : {}),
  }),
  head: () => ({
    meta: [
      { title: "Ask Olivia | People AI" },
      {
        name: "description",
        content: "Hiring, training, HR files and the rota, by asking.",
      },
    ],
  }),
  component: OliviaPage,
});

/** The workforce screen to show beside Olivia: the one last open in her workspace, else her
 * first. None for somebody who may talk to her but open none of them. */
function useScreenBeside(): string | null {
  const { navigation, me } = usePolicy();
  const last = lastPlaces(me.user.id).olivia;
  if (last && !last.startsWith("/ws/olivia") && workspaceFor(last, "olivia").id === "olivia") {
    return last;
  }
  const screens = workspaceNavigation(WORKSPACE_BY_ID.olivia, navigation);
  return screens.find((entry) => entry.id !== "workforce-agent")?.path ?? null;
}

/** Olivia's front door: her conversation in the right-hand pane, beside a workforce screen. */
function OliviaPage() {
  const { c, ask } = Route.useSearch();
  const navigate = useNavigate();
  const desk = useMedia(DESK_QUERY);
  const beside = useScreenBeside();

  useEffect(() => {
    if (!beside) return;
    openPaneAgent("olivia", desk, {
      threadKey: "olivia",
      threadId: c,
      ask,
      arriving: true,
    });
    void navigate({ to: beside, replace: true });
  }, [ask, beside, c, desk, navigate]);

  if (beside) return null;
  return (
    <>
      <h1 className="sr-only">Olivia</h1>
      <OliviaWorkspace
        threadId={c}
        ask={ask}
        onGo={(next, replace) =>
          void navigate({
            to: "/ws/olivia",
            search: next ? { c: next } : {},
            replace,
          })
        }
      />
    </>
  );
}
